import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { signOut } from "@/features/auth/api/identity-platform-auth";
import { fetchRuntimeSession } from "@/features/runtime-session/api/runtime-session-client";
import type { RuntimeSessionResponse } from "@/features/deployment-selection/model/runtime-session";
import { ApiError } from "@/shared/api/api-error";

/** Fraction of `expires_in` after which the runtime session should be refreshed. */
const REFRESH_AT_TTL_FRACTION = 0.8;

export function runtimeSessionQueryKey(deploymentId: string, operationIds: readonly string[]) {
  const operationsSignature = [...operationIds].sort().join(",");

  return ["runtimeSession", deploymentId, operationsSignature] as const;
}

/**
 * Fetches and keeps a runtime session (short-lived runtime API access token)
 * up to date for a deployment, refreshing it before it expires.
 *
 * `operationIds` should be a stable (memoized) array; an empty array disables
 * the query, since there is nothing to request a runtime session for.
 */
export function useRuntimeSession(deploymentId: string, operationIds: string[]) {
  const navigate = useNavigate();

  return useQuery<RuntimeSessionResponse, Error>({
    queryKey: runtimeSessionQueryKey(deploymentId, operationIds),
    enabled: operationIds.length > 0,
    queryFn: async () => {
      try {
        return await fetchRuntimeSession(deploymentId, operationIds);
      } catch (error) {
        if (error instanceof ApiError && error.statusCode === 401) {
          await signOut();
          navigate("/login", { replace: true });
          throw new Error("Authentication expired. Please sign in again.");
        }

        throw error;
      }
    },
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.statusCode === 401 || error.statusCode === 403)) {
        return false;
      }

      return failureCount < 2;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) {
        return false;
      }

      return data.expires_in * 1000 * REFRESH_AT_TTL_FRACTION;
    },
    refetchIntervalInBackground: false,
    // The TTL refresh already rotates the token. A focus refetch replaces the
    // RuntimeHttpClient and restarts every SSE stream unnecessarily.
    refetchOnWindowFocus: false,
    refetchOnReconnect: "always",
  });
}
